import React, { Fragment } from 'react';
import spacetime from 'spacetime';

import { guid, serialize, sorts } from '../utils';

import { getCategoryInfo, saveCategory, getCategory } from '../actions/categoryLanding';

import Alert from 'react-s-alert';
import HomeNav from '../components/HomeNav.jsx';
import Helmet from 'react-helmet';
import Input from '../components/form/FormikInput.jsx';
import FormikSelect from '../components/form/FormikSelect';
import TextArea from '../components/form/FormikTextArea.jsx';
import { useFormik } from 'formik';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MdMenu } from 'react-icons/md';
import classNames from 'classnames';
import LinkEdit from '../components/home/LinkEdit';
import ImageUploader from '../components/ImageUploader';
import BannerUploader from '../components/home/BannerUploader';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config';

const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',

    // change background colour if dragging
    background: isDragging ? 'lightgreen' : '',
    marginBottom: `1.25rem`,
    padding: '.75rem',
    // styles we need to apply on draggables
    ...draggableStyle
});

const getListStyle = (isDraggingOver) => ({});

const HomepageView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const url = queryParams.get('url');
    const queryDraft = queryParams.get('draft');
    const [enableReorder, setEnableReorder] = React.useState(false);
    const [category, setCategory] = React.useState({
        snapshots: []
    });

    const [drafts, setDrafts] = React.useState([]);

    React.useEffect(() => {
        let local = window.localStorage.getItem(url);
        let _drafts = [];
        if (local) {
            _drafts = JSON.parse(local).map((draft) => ({
                ...draft,
                snapshots: typeof draft.snapshots === 'string' ? JSON.parse(draft.snapshots) : draft.snapshots
            }));

            setDrafts(_drafts);
        }
        let _draft = queryDraft && _drafts[queryDraft];

        if (_draft) {
            let draft = {
                ..._draft,
                snapshots: typeof _draft.snapshots === 'string' ? JSON.parse(_draft.snapshots) : _draft.snapshots
            };
            setCategory(draft);
            return;
        }

        getCategoryInfo('shop-homepage')
            .then((resp) => {
                setCategory(resp.data);

                if (!resp.data.snapshots) {
                    setCategory((category) => ({ ...category, snapshots: {} }));
                }
            })
            .catch((e) => {
                getCategory('shop-homepage').then((resp) => {
                    setCategory(resp.data);
                });
            });
    }, [queryDraft, url]);

    const handlePublish = async (values) => {
        try {
            let isDraft = queryDraft && drafts[queryDraft];
            let snaps = values.snapshots.filter(Boolean).map((snap) => {
                if (!snap.id) snap.id = guid();
                snap.links = snap.links.map((link, index) => ({
                    ...link,
                    order: index
                }));
                return snap;
            });
            let data = {
                banner_text: [values.banner_link, values.banner_text].filter(Boolean).join(';'),
                banner_image: values.banner_image,
                snapshots: JSON.stringify(snaps)
            };
            await saveCategory('shop-homepage', data);
            Alert.success('Published!');
            if (isDraft) handleDeleteDraft();
        } catch {
            Alert.error('Error Publishing. Please contact Web Dev.');
        }
    };

    const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
        enableReinitialize: true,
        initialValues: {
            banner_image: category?.banner_image || '',
            banner_text: category?.banner_text?.split(';')[1] || '',
            banner_link: category?.banner_text?.split(';')[0] || '',
            draft_name: category?.draft_name,
            notes: category?.notes,
            snapshots: category.snapshots || []
        },
        onSubmit: handlePublish
    });

    const handleSaveDraft = () => {
        let isDraft = queryDraft && !!drafts[queryDraft];
        let _drafts = drafts;
        let timeStamp = Date.now();

        if (isDraft) {
            _drafts = _drafts.map((draft, index) => {
                let draftName = `Draft ${spacetime(draft.created || timeStamp).format('nice-day')}`;
                if (index === parseInt(queryDraft, 10)) {
                    draft = {
                        ...draft,
                        banner_text: [values.banner_link, values.banner_text].filter(Boolean).join(';'),
                        banner_image: values.banner_image,
                        snapshots: values.snapshots.filter(Boolean),
                        draft_name: values.draft_name || draft.draft_name || draftName,
                        notes: values.notes || '',
                        updated: timeStamp,
                        created: draft.created || timeStamp
                    };
                }
                return draft;
            });
        } else {
            _drafts = [
                ..._drafts,
                {
                    category_name: url,
                    banner_text: values.banner_text,
                    banner_image: values.banner_image,
                    snapshots: values.snapshots.filter(Boolean),
                    draft_name: `Draft ${spacetime(timeStamp).format('nice-day')}`,
                    notes: '',
                    updated: timeStamp,
                    created: timeStamp
                }
            ];
        }

        setDrafts(_drafts);

        window.localStorage.setItem(url, JSON.stringify(_drafts));

        if (!isDraft) {
            navigate({
                pathname: location.pathname,
                search: new URLSearchParams({
                    url,
                    draft: _drafts.length - 1
                }).toString()
            });
        }
        Alert.success('Draft Saved!');
    };

    const handleDeleteDraft = () => {
        let draftIndex = parseInt(queryDraft, 10);
        let newDrafts = drafts.filter((d, i) => i !== draftIndex);
        window.localStorage.setItem(url, JSON.stringify(newDrafts));
        let query = {
            url: url
        };
        window.location = `${window.location.pathname}?${serialize(query)}`;
    };

    let isDraft = queryDraft && drafts[queryDraft];

    let title = `Edit Shop Homepage ${
        isDraft ? `[DRAFT] UPDATED: ${spacetime(category.updated).format('nice-day')}` : ''
    }`;

    // a little function to help us with reordering the result
    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    let onLinksDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        let snap_ind = result.draggableId.split(':')[1];
        let items = reorder(values.snapshots[snap_ind].links, result.source.index, result.destination.index).map(
            (item, index) => ({ ...item, order: index })
        );

        setFieldValue(`snapshots[${snap_ind}].links`, items);
    };

    let onSnapsDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        let items = reorder(values.snapshots, result.source.index, result.destination.index).map((item, index) => ({
            ...item,
            order: index
        }));

        setFieldValue(`snapshots`, items);
    };

    let handleAddSection = () => {
        let newSnaps = [
            {
                title: '',
                id: guid(),
                product_line: '',
                links: []
            },
            ...values.snapshots
        ].map((snap, index) => ({
            ...snap,
            order: index
        }));

        setFieldValue('snapshots', newSnaps);
    };

    let handleRemoveSection = (id) => {
        if (
            window.confirm(
                "Are you sure? You will loose all settings for this section! Don't forget to publish your changes."
            )
        ) {
            let newSnaps = values.snapshots.filter((snap) => snap.id !== id);
            setFieldValue('snapshots', newSnaps);
        }
    };

    return (
        <Fragment>
            <HomeNav />
            <div className="container-fluid py-10">
                <Helmet>
                    <title>{title}</title>
                </Helmet>
                <div className="row">
                    <div className="col-xs-12 col-md-8 first-md">
                        <h1 className="mb-5">{title}</h1>

                        {!enableReorder && (
                            <div className="mb-4">
                                <h2>Edit Banners</h2>
                                <BannerUploader
                                    image={values.banner_image}
                                    categorySlug="shop-homepage"
                                    onChange={(url) => setFieldValue('banner_image', url)}
                                />
                            </div>
                        )}

                        <Input
                            id="banner_text"
                            label="Banner Alt Text"
                            value={values.banner_text}
                            onChange={handleChange}
                        />
                        <Input
                            id="banner_link"
                            label="Banner Link"
                            value={values.banner_link}
                            onChange={handleChange}
                        />

                        <h2>Edit Product Boxes</h2>
                        <div className="mb-4">
                            <button
                                className="button button-secondary"
                                type="button"
                                onClick={() => setEnableReorder(!enableReorder)}
                            >
                                {!enableReorder && 'Reorder Product Boxes'}
                                {enableReorder && 'Finish'}
                            </button>
                            {!enableReorder && (
                                <button className="button button-secondary" type="button" onClick={handleAddSection}>
                                    Add Product Box
                                </button>
                            )}
                        </div>

                        <DragDropContext onDragEnd={onSnapsDragEnd} isDropDisabled={!enableReorder}>
                            <Droppable droppableId={`snapshots`} isDropDisabled={!enableReorder}>
                                {(provided, snapshot) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        style={getListStyle(snapshot.isDraggingOver)}
                                    >
                                        {values.snapshots
                                            .sort((a, b) => sorts.asc(a.order, b.order))
                                            .map((snap, index) => {
                                                return (
                                                    <Draggable
                                                        key={snap.id}
                                                        draggableId={snap.id}
                                                        index={index}
                                                        isDragDisabled={!enableReorder}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                style={getItemStyle(
                                                                    snapshot.isDragging,
                                                                    provided.draggableProps.style
                                                                )}
                                                            >
                                                                <div>
                                                                    {enableReorder && (
                                                                        <h3
                                                                            className={classNames({
                                                                                'mb-0': enableReorder
                                                                            })}
                                                                        >
                                                                            <MdMenu />
                                                                            {snap.title || snap.id}
                                                                        </h3>
                                                                    )}
                                                                    {!enableReorder && (
                                                                        <div>
                                                                            <ImageUploader
                                                                                bucket="shop_homepage_snaps"
                                                                                id={snap.id}
                                                                                image={values.snapshots[index].image}
                                                                                onUpload={(url) => {
                                                                                    setFieldValue(
                                                                                        `snapshots[${index}].image`,
                                                                                        url
                                                                                    );
                                                                                }}
                                                                                unique
                                                                                showWarning={false}
                                                                            />

                                                                            <Input
                                                                                label="Title"
                                                                                id={`snapshots[${index}].title`}
                                                                                value={values.snapshots[index].title}
                                                                                onChange={handleChange}
                                                                            />
                                                                            <Input
                                                                                label="Tagline"
                                                                                id={`snapshots[${index}].subhead`}
                                                                                value={values.snapshots[index].subhead}
                                                                                onChange={handleChange}
                                                                            />
                                                                            <FormikSelect
                                                                                id={`snapshots[${index}].product_line`}
                                                                                label="Product Line"
                                                                                value={
                                                                                    values.snapshots[index].product_line
                                                                                }
                                                                                onChange={handleChange}
                                                                            >
                                                                                {config.product_lines.map((line) => (
                                                                                    <option
                                                                                        key={`option_${line}`}
                                                                                        value={line}
                                                                                    >
                                                                                        {line}
                                                                                    </option>
                                                                                ))}
                                                                            </FormikSelect>
                                                                            <div className="mb-10">
                                                                                <button
                                                                                    className="button button-danger btn-sm"
                                                                                    onClick={() =>
                                                                                        handleRemoveSection(snap.id)
                                                                                    }
                                                                                >
                                                                                    Remove Section
                                                                                </button>
                                                                            </div>
                                                                            <h3>Links</h3>
                                                                            <p>
                                                                                Use full <code>https://</code> URLs for
                                                                                external links, and aboslute{' '}
                                                                                <code>/</code> paths for internal links.
                                                                            </p>
                                                                            <DragDropContext onDragEnd={onLinksDragEnd}>
                                                                                <Droppable
                                                                                    droppableId={`snapshot[${index}].links`}
                                                                                >
                                                                                    {(provided, snapshot) => (
                                                                                        <div
                                                                                            {...provided.droppableProps}
                                                                                            ref={provided.innerRef}
                                                                                            style={getListStyle(
                                                                                                snapshot.isDraggingOver
                                                                                            )}
                                                                                        >
                                                                                            {snap?.links
                                                                                                ?.filter(Boolean)
                                                                                                .sort((a, b) =>
                                                                                                    sorts.asc(
                                                                                                        a.order,
                                                                                                        b.order
                                                                                                    )
                                                                                                )
                                                                                                .map(
                                                                                                    (
                                                                                                        item,
                                                                                                        link_index
                                                                                                    ) => (
                                                                                                        <Draggable
                                                                                                            key={`snapshots[${index}].links[${link_index}]`}
                                                                                                            draggableId={`snapshots:${index}:${link_index}`}
                                                                                                            index={
                                                                                                                link_index
                                                                                                            }
                                                                                                        >
                                                                                                            {(
                                                                                                                provided,
                                                                                                                snapshot
                                                                                                            ) => (
                                                                                                                <div
                                                                                                                    ref={
                                                                                                                        provided.innerRef
                                                                                                                    }
                                                                                                                    {...provided.draggableProps}
                                                                                                                    {...provided.dragHandleProps}
                                                                                                                    style={getItemStyle(
                                                                                                                        snapshot.isDragging,
                                                                                                                        provided
                                                                                                                            .draggableProps
                                                                                                                            .style
                                                                                                                    )}
                                                                                                                >
                                                                                                                    <LinkEdit
                                                                                                                        values={
                                                                                                                            values
                                                                                                                        }
                                                                                                                        index={
                                                                                                                            index
                                                                                                                        }
                                                                                                                        linkIndex={
                                                                                                                            link_index
                                                                                                                        }
                                                                                                                        onChange={
                                                                                                                            handleChange
                                                                                                                        }
                                                                                                                        setFieldValue={
                                                                                                                            setFieldValue
                                                                                                                        }
                                                                                                                        onRemove={() => {
                                                                                                                            let links =
                                                                                                                                values.snapshots[
                                                                                                                                    index
                                                                                                                                ].links.slice(
                                                                                                                                    0,
                                                                                                                                    -1
                                                                                                                                );
                                                                                                                            setFieldValue(
                                                                                                                                `snapshots[${index}].links`,
                                                                                                                                links
                                                                                                                            );
                                                                                                                        }}
                                                                                                                    />
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </Draggable>
                                                                                                    )
                                                                                                )}
                                                                                        </div>
                                                                                    )}
                                                                                </Droppable>
                                                                            </DragDropContext>
                                                                            <button
                                                                                className="button button-secondary btn-sm"
                                                                                onClick={() => {
                                                                                    setFieldValue(
                                                                                        `snapshots[${index}].links[${values.snapshots[index].links.length}]`,
                                                                                        {
                                                                                            url: '',
                                                                                            display_name: '',
                                                                                            order: values.snapshots[
                                                                                                index
                                                                                            ].links.length
                                                                                        }
                                                                                    );
                                                                                }}
                                                                            >
                                                                                Add Link
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    <hr />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                    <div className="col-xs-12 first-xs col-md-4 spaced-xs collapse-md category-edit-sidebar-wrapper">
                        <div className="category-edit-sidebar">
                            <FormikSelect
                                label="Versions"
                                value={queryDraft}
                                onChange={(event) => {
                                    let value = event.target.value;
                                    let query = {
                                        url: url
                                    };
                                    if (value > -1) {
                                        query.draft = value;
                                    }
                                    window.location = `${window.location.pathname}?${serialize(query)}`;
                                }}
                            >
                                <option value={-1}>Live Production</option>
                                {drafts.map((draft, i) => (
                                    <option key={`draft_${i}`} value={i}>
                                        {draft.draft_name}
                                    </option>
                                ))}
                            </FormikSelect>
                            {isDraft && (
                                <div>
                                    <Input
                                        label="Draft Name"
                                        id="draft_name"
                                        value={values.draft_name}
                                        onChange={handleChange}
                                    />
                                    <TextArea
                                        id="notes"
                                        rows={4}
                                        label="Draft Notes"
                                        value={values.notes}
                                        onChange={handleChange}
                                    />
                                </div>
                            )}
                            <button
                                type="button"
                                className="button button-success full-xs mb-5"
                                disabled={enableReorder}
                                onClick={handleSubmit}
                            >
                                Publish
                            </button>

                            <button
                                type="button"
                                className="button full-xs mb-5"
                                disabled={enableReorder}
                                onClick={handleSaveDraft}
                            >
                                Save Draft
                            </button>
                            {isDraft && (
                                <button
                                    type="button"
                                    className="button button-danger full-xs mb-5"
                                    onClick={handleDeleteDraft}
                                >
                                    Delete Draft
                                </button>
                            )}
                            {!isDraft && (
                                <p>
                                    <a href={`https://shop.playaway.com`} target="_blank" rel="noopener noreferrer">
                                        View Published Page
                                    </a>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
export default HomepageView;
